// Offline activation — the operator console's RESPOND screen (OF-170).
//
// An air-gapped install cannot reach this server, so its licence is activated by
// carrying two files across the gap. On the install the operator generates a
// CHALLENGE (platform console → Licence → Offline activation, or
// scripts/ops/offline-activation.sh challenge). They bring it here; this screen
// shows what it asks for and, on confirmation, issues the SIGNED RESPONSE
// through POST /offline/respond. They carry the response back and the install
// verifies it against its own licence public key before it stores anything.
//
// WHAT THIS SCREEN DECIDES: nothing. Whether the licence is live, whether the
// challenge is fresh, whether the code is already bound elsewhere and how long
// the window is are all the server's call, on the verified session, and its
// refusal is shown verbatim. The screen only declines, before the round trip,
// what is plainly not a challenge (so the operator is told in their own terms),
// and never prints the licence key that travels inside the response.
//
// The response file IS the licence key plus a signature. The page says so, and
// hands it over as a download rather than showing it.
import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '@/lib/supabase';
import { PageHeader, Card, CardHead, Btn, Mono } from '@/components/shared/primitives';
import { Icon } from '@/components/shared/Icon';

const CHALLENGE_FORMAT = 'vendorwatch.offline-challenge/1';
const CODE_RE = /^[0-9A-F]{6}(-[0-9A-F]{6}){3}$/;
const LICENSE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{5,63}$/;
const INSTANCE_ID_RE = /^[A-Za-z0-9._:-]{1,64}$/;
const ISO_UTC_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

interface ChallengeFile {
  format: string;
  challenge_code: string;
  license_id: string;
  instance_id: string;
  created_at: string;
  expires_at: string;
  product: string;
  product_version?: string;
}

interface RespondAnswer {
  response: unknown;
  filename: string;
  validityDays: number;
  license: { license_id: string; org_name: string | null; tier: string; expires_at: string; scope?: string };
}

type Parsed =
  | { kind: 'empty' }
  | { kind: 'bad'; message: string }
  | { kind: 'ok'; challenge: ChallengeFile };

/** What is plainly wrong with the text, in the operator's terms; the server remains the authority. */
function parseChallenge(text: string): Parsed {
  const t = text.trim();
  if (!t) return { kind: 'empty' };
  let v: unknown;
  try {
    v = JSON.parse(t);
  } catch {
    return { kind: 'bad', message: 'The challenge is not valid JSON. Use the file exactly as the install produced it.' };
  }
  if (!v || typeof v !== 'object' || Array.isArray(v) || (v as { format?: unknown }).format !== CHALLENGE_FORMAT) {
    return { kind: 'bad', message: 'This is not a VendorWatch offline-activation challenge file.' };
  }
  const c = v as Record<string, unknown>;
  const field = (name: string, rx: RegExp, label: string): string | null =>
    typeof c[name] === 'string' && rx.test(c[name] as string) ? null : `The ${label} in the challenge is not in the expected form.`;
  const problem =
    field('challenge_code', CODE_RE, 'challenge code') ??
    field('license_id', LICENSE_ID_RE, 'licence ID') ??
    field('instance_id', INSTANCE_ID_RE, 'install ID') ??
    field('created_at', ISO_UTC_RE, 'creation time') ??
    field('expires_at', ISO_UTC_RE, 'expiry time');
  if (problem) return { kind: 'bad', message: problem };
  if (Date.parse(c.expires_at as string) <= Date.now()) {
    return { kind: 'bad', message: 'This challenge has expired. Generate a new one on the install.' };
  }
  return { kind: 'ok', challenge: v as ChallengeFile };
}

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(new Error('The file could not be read.'));
    r.readAsText(file);
  });
}

function download(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const fmt = (iso: string) => new Date(iso).toLocaleString();
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 };
const field: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontFamily: 'var(--mono)', fontSize: 12.5 };
const dl: React.CSSProperties = { display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 8, columnGap: 12, fontSize: 13, margin: 0 };

export function OfflineActivationPage() {
  const [pasted, setPasted] = useState('');
  const [fileText, setFileText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<RespondAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);

  const text = pasted.trim() ? pasted : fileText;
  const parsed = useMemo(() => parseChallenge(text), [text]);

  // A different challenge is a different question: drop the previous answer.
  useEffect(() => {
    setAnswer(null);
    setError(null);
  }, [text]);

  async function onFile(files: FileList | null) {
    const f = files?.[0];
    if (!f) return;
    try {
      setFileText(await readFileText(f));
      setFileName(f.name);
    } catch (e) {
      setFileText('');
      setFileName(null);
      setError(e instanceof Error ? e.message : 'The file could not be read.');
    }
  }

  async function issue() {
    if (parsed.kind !== 'ok') return;
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      const a = await apiRequest<RespondAnswer>('/offline/respond', {
        method: 'POST',
        body: JSON.stringify({ challenge: parsed.challenge }),
      });
      setAnswer(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The response could not be issued');
    } finally {
      setBusy(false);
    }
  }

  const problem = parsed.kind === 'bad' ? parsed.message : error;

  return (
    <div>
      <PageHeader
        title="Offline activation"
        subtitle="Answer an air-gapped install's challenge with a signed response it can verify without a network"
      />

      <Card pad={20} style={{ marginBottom: 16 }}>
        <CardHead title="1. Load the challenge" sub="Generated on the install (platform console → Licence → Offline activation, or scripts/ops/offline-activation.sh challenge)" />
        <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
          <div>
            <label htmlFor="oa-file" style={lbl}>Challenge file</label>
            <input id="oa-file" type="file" accept=".json,application/json" onChange={(e) => onFile(e.target.files)} />
            {fileName && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Selected: {fileName}</div>}
          </div>
          <div>
            <label htmlFor="oa-paste" style={lbl}>Or paste the challenge file</label>
            <textarea
              id="oa-paste"
              rows={5}
              spellCheck={false}
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder='{"format":"vendorwatch.offline-challenge/1", …}'
              style={{ ...field, resize: 'vertical' }}
            />
          </div>
          {problem && (
            <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--status-expired, #b91c1c)' }}>{problem}</p>
          )}
        </div>
      </Card>

      {parsed.kind === 'ok' && (
        <Card pad={20} style={{ marginBottom: 16 }}>
          <CardHead title="2. Check what it asks for" sub="You are about to issue a signed licence response for this install" />
          <dl style={{ ...dl, marginTop: 14 }}>
            <dt style={{ color: 'var(--text-secondary)' }}>Licence ID</dt>
            <dd style={{ margin: 0 }}><Mono>{parsed.challenge.license_id}</Mono></dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Install</dt>
            <dd style={{ margin: 0 }}><Mono>{parsed.challenge.instance_id}</Mono></dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Challenge code</dt>
            <dd style={{ margin: 0 }}><Mono>{parsed.challenge.challenge_code}</Mono></dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Generated</dt>
            <dd style={{ margin: 0 }}>{fmt(parsed.challenge.created_at)}</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Challenge expires</dt>
            <dd style={{ margin: 0 }}>{fmt(parsed.challenge.expires_at)}</dd>
          </dl>
          <div style={{ marginTop: 16 }}>
            <Btn kind="primary" icon="file-key" onClick={issue} disabled={busy}>Issue signed response</Btn>
          </div>
        </Card>
      )}
      {parsed.kind !== 'ok' && (
        <div style={{ marginBottom: 16 }}>
          <Btn kind="primary" icon="file-key" disabled>Issue signed response</Btn>
        </div>
      )}

      {answer && (
        <Card pad={20}>
          <CardHead title="Response issued" sub="Carry this file back to the install and redeem it there" />
          <dl style={{ ...dl, marginTop: 14 }}>
            <dt style={{ color: 'var(--text-secondary)' }}>Organisation</dt>
            <dd style={{ margin: 0 }}>{answer.license.org_name ?? '—'} ({answer.license.tier}{answer.license.scope ? `, ${answer.license.scope} scope` : ''})</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Offline window</dt>
            <dd style={{ margin: 0 }}>{answer.validityDays} days from now</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>Licence expires</dt>
            <dd style={{ margin: 0 }}>{fmt(answer.license.expires_at)}</dd>
            <dt style={{ color: 'var(--text-secondary)' }}>File</dt>
            <dd style={{ margin: 0 }}><Mono>{answer.filename}</Mono></dd>
          </dl>
          <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 8, background: 'var(--warning-bg, #FFFBEB)', border: '1px solid var(--warning-border, #FDE68A)', fontSize: 13, display: 'flex', gap: 8 }}>
            <Icon name="alert-triangle" size={15} />
            <span>This file contains the licence key. Treat it like the key itself: carry it on removable media and delete it once the install has activated.</span>
          </div>
          <div style={{ marginTop: 16 }}>
            <Btn kind="primary" icon="download" onClick={() => download(answer.filename, JSON.stringify(answer.response, null, 2) + '\n')}>
              Download response file
            </Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

export default OfflineActivationPage;
